import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MentionTextarea } from '../components/MentionTextarea';
import { NoteContentRenderer } from '../components/NoteContentRenderer';
import { ReferenceCard } from '../components/ReferenceCard';
import { toast } from 'sonner';
import { 
  StickyNote, Plus, Search, Folder, FolderPlus, Edit, Trash2, Pin, 
  Mic, MicOff, Volume2, X, Save, Tag, Calendar, AlertCircle, Users,
  LayoutGrid, List, Archive, Download, BarChart3, FileText, CheckSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  low: 'bg-gray-500',
  medium: 'bg-yellow-500',
  high: 'bg-red-500'
};

export const DeveloperNotes = () => {
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showArchived, setShowArchived] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState([]);
  
  // Dialogs
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'move' or 'tag'
  const [bulkActionData, setBulkActionData] = useState({ project_id: '', tags: '' });
  
  // Speech
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  
  // Form data
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });
  
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    project_id: null,
    tags: [],
    priority: 'medium',
    is_pinned: false,
    members: [],
    teams: [],
    assigned_to: null,
    references: []
  });
  
  const [tagInput, setTagInput] = useState('');
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAllUsers();
    fetchAllTeams();
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [selectedProject, searchQuery, showArchived]);

  const fetchData = async () => {
    try {
      const projectsRes = await axios.get(`${API}/note-projects`);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const params = {};
      if (selectedProject !== 'all') {
        params.project_id = selectedProject;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const notesRes = await axios.get(`${API}/developer-notes`, { params });
      // Filter based on archived status
      const filteredNotes = showArchived 
        ? notesRes.data.filter(note => note.is_archived)
        : notesRes.data.filter(note => !note.is_archived);
      setNotes(filteredNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    }
  };

  // Project CRUD
  const handleCreateProject = async () => {
    try {
      await axios.post(`${API}/note-projects`, projectForm);
      toast.success('Project created successfully');
      setProjectDialogOpen(false);
      setProjectForm({ name: '', description: '', color: '#3b82f6' });
      fetchData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleUpdateProject = async () => {
    try {
      await axios.put(`${API}/note-projects/${editingProject.id}`, projectForm);
      toast.success('Project updated successfully');
      setProjectDialogOpen(false);
      setEditingProject(null);
      fetchData();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project? Notes will be moved to "No Project".')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/note-projects/${projectId}`);
      toast.success('Project deleted successfully');
      if (selectedProject === projectId) {
        setSelectedProject('all');
      }
      fetchData();
      fetchNotes();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const openProjectDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setProjectForm({
        name: project.name,
        description: project.description || '',
        color: project.color || '#3b82f6'
      });
    } else {
      setEditingProject(null);
      setProjectForm({ name: '', description: '', color: '#3b82f6' });
    }
    setProjectDialogOpen(true);
  };

  // Note CRUD
  const handleCreateNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      await axios.post(`${API}/developer-notes`, noteForm);
      toast.success('Note created successfully');
      setNoteDialogOpen(false);
      resetNoteForm();
      fetchNotes();
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Failed to create note');
    }
  };

  const handleUpdateNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      await axios.put(`${API}/developer-notes/${selectedNote.id}`, noteForm);
      toast.success('Note updated successfully');
      setNoteDialogOpen(false);
      setSelectedNote(null);
      resetNoteForm();
      fetchNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/developer-notes/${noteId}`);
      toast.success('Note deleted successfully');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  // Archive/Unarchive Note
  const handleArchiveNote = async (noteId, archived) => {
    try {
      await axios.patch(`${API}/developer-notes/${noteId}/archive`, null, {
        params: { archived }
      });
      toast.success(archived ? 'Note archived' : 'Note restored');
      fetchNotes();
      if (selectedNote && selectedNote.id === noteId) {
        setNoteDialogOpen(false);
        setSelectedNote(null);
      }
    } catch (error) {
      console.error('Error archiving note:', error);
      toast.error('Failed to archive note');
    }
  };

  // Export Single Note
  const handleExportNote = async (noteId, title) => {
    try {
      const response = await axios.get(`${API}/developer-notes/${noteId}/export/markdown`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.md`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Note exported successfully');
    } catch (error) {
      console.error('Error exporting note:', error);
      toast.error('Failed to export note');
    }
  };

  // Export All Notes
  const handleExportAllNotes = async (format = 'markdown') => {
    try {
      const response = await axios.get(`${API}/developer-notes/export/all`, {
        params: { format },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'json' ? 'notes_export.json' : 'all_notes.md');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('All notes exported successfully');
    } catch (error) {
      console.error('Error exporting all notes:', error);
      toast.error('Failed to export notes');
    }
  };

  // Fetch Statistics
  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/developer-notes/stats`);
      setStats(response.data);
      setStatsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    }
  };

  // Bulk Operations
  const toggleNoteSelection = (noteId) => {
    setSelectedNotes(prev => 
      prev.includes(noteId) 
        ? prev.filter(id => id !== noteId)
        : [...prev, noteId]
    );
  };

  const selectAllNotes = () => {
    if (selectedNotes.length === notes.length) {
      setSelectedNotes([]);
    } else {
      setSelectedNotes(notes.map(n => n.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotes.length === 0) {
      toast.error('No notes selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedNotes.length} note(s)?`)) {
      return;
    }

    try {
      await axios.post(`${API}/developer-notes/bulk-delete`, selectedNotes);
      toast.success(`${selectedNotes.length} note(s) deleted`);
      setSelectedNotes([]);
      setBulkSelectMode(false);
      fetchNotes();
    } catch (error) {
      console.error('Error deleting notes:', error);
      toast.error('Failed to delete notes');
    }
  };

  const handleBulkMove = async () => {
    if (!bulkActionData.project_id) {
      toast.error('Please select a project');
      return;
    }

    try {
      await axios.post(`${API}/developer-notes/bulk-move`, {
        note_ids: selectedNotes,
        project_id: bulkActionData.project_id
      });
      toast.success(`${selectedNotes.length} note(s) moved`);
      setSelectedNotes([]);
      setBulkSelectMode(false);
      setBulkActionDialogOpen(false);
      setBulkActionData({ project_id: '', tags: '' });
      fetchNotes();
    } catch (error) {
      console.error('Error moving notes:', error);
      toast.error('Failed to move notes');
    }
  };

  const handleBulkTag = async () => {
    if (!bulkActionData.tags) {
      toast.error('Please enter tags');
      return;
    }

    const tagsArray = bulkActionData.tags.split(',').map(t => t.trim()).filter(Boolean);
    if (tagsArray.length === 0) {
      toast.error('Please enter valid tags');
      return;
    }

    try {
      await axios.post(`${API}/developer-notes/bulk-tag`, {
        note_ids: selectedNotes,
        tags: tagsArray
      });
      toast.success(`Tags added to ${selectedNotes.length} note(s)`);
      setSelectedNotes([]);
      setBulkSelectMode(false);
      setBulkActionDialogOpen(false);
      setBulkActionData({ project_id: '', tags: '' });
      fetchNotes();
    } catch (error) {
      console.error('Error tagging notes:', error);
      toast.error('Failed to add tags');
    }
  };

  const openNoteDialog = (note = null) => {
    if (note) {
      setSelectedNote(note);
      setNoteForm({
        title: note.title,
        content: note.content,
        project_id: note.project_id,
        tags: note.tags || [],
        priority: note.priority,
        is_pinned: note.is_pinned,
        members: note.members || [],
        assigned_to: note.assigned_to,
        references: note.references || []
      });
    } else {
      setSelectedNote(null);
      resetNoteForm();
    }
    setNoteDialogOpen(true);
  };

  const resetNoteForm = () => {
    setNoteForm({
      title: '',
      content: '',
      project_id: selectedProject !== 'all' && selectedProject !== 'none' ? selectedProject : null,
      tags: [],
      priority: 'medium',
      is_pinned: false,
      members: [],
      assigned_to: null,
      references: []
    });
    setTagInput('');
    setSelectedMember('');
    setSelectedAssignee('');
  };

  // Fetch all users for dropdown
  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/users/search`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch all teams for dropdown
  const fetchAllTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setAllTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Add member to note
  const addMemberToNote = async (noteId, userId) => {
    const user = allUsers.find(u => u.user_id === userId);
    if (!user) return;
    
    try {
      await axios.post(`${API}/developer-notes/${noteId}/members`, null, {
        params: { member_email: user.email }
      });
      toast.success('Member added successfully');
      setSelectedMember('');
      await fetchNotes();
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/developer-notes/${noteId}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  // Remove member from note
  const removeMemberFromNote = async (noteId, memberId) => {
    try {
      await axios.delete(`${API}/developer-notes/${noteId}/members/${memberId}`);
      toast.success('Member removed successfully');
      await fetchNotes();
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/developer-notes/${noteId}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  // Add member to project
  const addMemberToProject = async (projectId, userId) => {
    const user = allUsers.find(u => u.user_id === userId);
    if (!user) return;
    
    try {
      await axios.post(`${API}/note-projects/${projectId}/members`, null, {
        params: { member_email: user.email }
      });
      toast.success('Member added to project');
      setSelectedMember('');
      await fetchData();
      // Refresh the editing project
      const updatedProjects = await axios.get(`${API}/note-projects`);
      const updatedProject = updatedProjects.data.find(p => p.id === projectId);
      if (updatedProject) {
        setEditingProject(updatedProject);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  // Remove member from project
  const removeMemberFromProject = async (projectId, memberId) => {
    try {
      await axios.delete(`${API}/note-projects/${projectId}/members/${memberId}`);
      toast.success('Member removed from project');
      await fetchData();
      // Refresh the editing project
      const updatedProjects = await axios.get(`${API}/note-projects`);
      const updatedProject = updatedProjects.data.find(p => p.id === projectId);
      if (updatedProject) {
        setEditingProject(updatedProject);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  // Assign note
  const handleAssignNote = async (noteId, userId) => {
    try {
      await axios.put(`${API}/developer-notes/${noteId}/assign`, null, {
        params: { assigned_to: userId }
      });
      toast.success('Note assigned successfully');
      await fetchNotes();
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/developer-notes/${noteId}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Error assigning note:', error);
      toast.error('Failed to assign note');
    }
  };

  // Add team to note
  const addTeamToNote = async (noteId, teamId) => {
    try {
      await axios.post(`${API}/developer-notes/${noteId}/teams`, null, {
        params: { team_id: teamId }
      });
      toast.success('Team assigned successfully');
      setSelectedAssignee('');
      await fetchNotes();
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/developer-notes/${noteId}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign team');
    }
  };

  // Remove team from note
  const removeTeamFromNote = async (noteId, teamId) => {
    try {
      await axios.delete(`${API}/developer-notes/${noteId}/teams/${teamId}`);
      toast.success('Team removed successfully');
      await fetchNotes();
      // Refresh the selected note
      const updatedNote = await axios.get(`${API}/developer-notes/${noteId}`);
      setSelectedNote(updatedNote.data);
    } catch (error) {
      console.error('Error removing team:', error);
      toast.error('Failed to remove team');
    }
  };

  // Add team to project
  const addTeamToProject = async (projectId, teamId) => {
    try {
      await axios.post(`${API}/note-projects/${projectId}/teams`, null, {
        params: { team_id: teamId }
      });
      toast.success('Team assigned to project');
      setSelectedAssignee('');
      await fetchData();
      // Refresh the editing project
      const updatedProjects = await axios.get(`${API}/note-projects`);
      const updatedProject = updatedProjects.data.find(p => p.id === projectId);
      if (updatedProject) {
        setEditingProject(updatedProject);
      }
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign team');
    }
  };

  // Remove team from project
  const removeTeamFromProject = async (projectId, teamId) => {
    try {
      await axios.delete(`${API}/note-projects/${projectId}/teams/${teamId}`);
      toast.success('Team removed from project');
      await fetchData();
      // Refresh the editing project
      const updatedProjects = await axios.get(`${API}/note-projects`);
      const updatedProject = updatedProjects.data.find(p => p.id === projectId);
      if (updatedProject) {
        setEditingProject(updatedProject);
      }
    } catch (error) {
      console.error('Error removing team:', error);
      toast.error('Failed to remove team');
    }
  };

  // Tags
  const addTag = () => {
    if (tagInput.trim() && !noteForm.tags.includes(tagInput.trim())) {
      setNoteForm({
        ...noteForm,
        tags: [...noteForm.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNoteForm({
      ...noteForm,
      tags: noteForm.tags.filter(t => t !== tag)
    });
  };

  // Speech-to-Text (Browser Web Speech API)
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;

    recognitionRef.current.onstart = () => {
      setIsListening(true);
      toast.success('Listening...');
    };

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setNoteForm(prev => ({
          ...prev,
          content: prev.content + finalTranscript
        }));
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      toast.error('Speech recognition error: ' + event.error);
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      toast.info('Stopped listening');
    }
  };

  // Text-to-Speech
  const speakText = (text) => {
    if (!text) {
      toast.error('No text to speak');
      return;
    }

    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      utterance.onstart = () => {
        setIsSpeaking(true);
      };
      
      utterance.onend = () => {
        setIsSpeaking(false);
      };
      
      utterance.onerror = () => {
        setIsSpeaking(false);
        toast.error('Error speaking text');
      };
      
      window.speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech not supported in this browser');
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading notes...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar - Projects */}
        <div className="w-64 border-r bg-muted/30 p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Folder className="h-5 w-5" />
              Projects
            </h2>
            <Button size="sm" variant="ghost" onClick={() => openProjectDialog()}>
              <FolderPlus className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-1">
            <button
              onClick={() => setSelectedProject('all')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                selectedProject === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>All Notes</span>
                <Badge variant="secondary">{notes.length}</Badge>
              </div>
            </button>

            <button
              onClick={() => setSelectedProject('none')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                selectedProject === 'none' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>No Project</span>
                <Badge variant="secondary">
                  {notes.filter(n => !n.project_id).length}
                </Badge>
              </div>
            </button>

            <div className="border-t my-2"></div>

            {projects.map(project => (
              <div
                key={project.id}
                className={`group rounded-lg transition-colors ${
                  selectedProject === project.id ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                }`}
              >
                <button
                  onClick={() => setSelectedProject(project.id)}
                  className="w-full text-left px-3 py-2 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: project.color }}
                    ></div>
                    <span className="truncate">{project.name}</span>
                  </div>
                  <Badge variant="secondary">{project.note_count}</Badge>
                </button>
                <div className="flex gap-1 px-2 pb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProjectDialog(project);
                    }}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content - Notes */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="border-b p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <StickyNote className="h-8 w-8 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold">Notes</h1>
                  <p className="text-sm text-muted-foreground">
                    {selectedProject === 'all' 
                      ? 'All your notes' 
                      : selectedProject === 'none'
                      ? 'Notes without project'
                      : projects.find(p => p.id === selectedProject)?.name || 'Project notes'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant={bulkSelectMode ? 'secondary' : 'outline'} 
                  size="sm" 
                  onClick={() => {
                    setBulkSelectMode(!bulkSelectMode);
                    setSelectedNotes([]);
                  }}
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  {bulkSelectMode ? 'Cancel Select' : 'Select'}
                </Button>
                <Button variant="outline" size="sm" onClick={fetchStatistics}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Stats
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const format = window.confirm('Export as JSON? (Cancel for Markdown)') ? 'json' : 'markdown';
                  handleExportAllNotes(format);
                }}>
                  <Download className="h-4 w-4 mr-2" />
                  Export All
                </Button>
                <Button onClick={() => openNoteDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Note
                </Button>
              </div>
            </div>

            {/* Bulk Actions Toolbar */}
            {bulkSelectMode && selectedNotes.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{selectedNotes.length} note(s) selected</span>
                    <Button variant="outline" size="sm" onClick={selectAllNotes}>
                      {selectedNotes.length === notes.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      setBulkAction('move');
                      setBulkActionDialogOpen(true);
                    }}>
                      <Folder className="h-4 w-4 mr-2" />
                      Move to Project
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      setBulkAction('tag');
                      setBulkActionDialogOpen(true);
                    }}>
                      <Tag className="h-4 w-4 mr-2" />
                      Add Tags
                    </Button>
                    <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Search and View Toggle */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant={showArchived ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="h-10 px-3"
              >
                <Archive className="h-4 w-4 mr-2" />
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <div className="flex gap-1 border rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-8 px-3"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-8 px-3"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Notes Grid/List */}
          <div className="flex-1 overflow-y-auto p-4">
            {notes.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <StickyNote className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-xl font-semibold mb-2">No notes yet</p>
                  <p className="text-muted-foreground mb-4">
                    Create your first note to get started
                  </p>
                  <Button onClick={() => openNoteDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Note
                  </Button>
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {notes.map(note => (
                  <Card
                    key={note.id}
                    className={`cursor-pointer hover:shadow-lg transition-shadow group ${
                      bulkSelectMode && selectedNotes.includes(note.id) ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => bulkSelectMode ? toggleNoteSelection(note.id) : openNoteDialog(note)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        {bulkSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedNotes.includes(note.id)}
                            onChange={() => toggleNoteSelection(note.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1 h-4 w-4 cursor-pointer"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {note.is_pinned && <Pin className="h-4 w-4 text-blue-600" />}
                            <span className="truncate">{note.title}</span>
                          </CardTitle>
                          {note.project_name && (
                            <CardDescription className="text-xs mt-1">
                              <Folder className="h-3 w-3 inline mr-1" />
                              {note.project_name}
                            </CardDescription>
                          )}
                        </div>
                        <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[note.priority]}`}></div>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <div className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        <NoteContentRenderer content={note.content || 'No content'} references={note.references || []} showCards={false} />
                      </div>
                      
                      {note.tags && note.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {note.tags.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {note.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{note.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {note.member_details && note.member_details.length > 0 && (
                        <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground">
                          <span className="font-medium">Shared with:</span>
                          {note.member_details.slice(0, 2).map((member, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {member.first_name || member.email}
                            </Badge>
                          ))}
                          {note.member_details.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{note.member_details.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}

                      {note.assigned_to_details && (
                        <div className="flex items-center gap-1 mb-2 text-xs">
                          <span className="text-muted-foreground">Assigned to:</span>
                          <Badge variant="secondary" className="text-xs">
                            {note.assigned_to_details.first_name} {note.assigned_to_details.last_name}
                          </Badge>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(note.updated_at).toLocaleDateString()}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportNote(note.id, note.title);
                            }}
                            title="Export as Markdown"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveNote(note.id, !note.is_archived);
                            }}
                            title={note.is_archived ? 'Restore' : 'Archive'}
                          >
                            <Archive className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              speakText(note.content);
                            }}
                            title="Read aloud"
                          >
                            <Volume2 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note.id);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              // List View
              <div className="space-y-2">
                {notes.map(note => (
                  <Card
                    key={note.id}
                    className={`cursor-pointer hover:shadow-md transition-shadow group ${
                      bulkSelectMode && selectedNotes.includes(note.id) ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => bulkSelectMode ? toggleNoteSelection(note.id) : openNoteDialog(note)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Bulk Select Checkbox */}
                        {bulkSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedNotes.includes(note.id)}
                            onChange={() => toggleNoteSelection(note.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1 h-4 w-4 cursor-pointer"
                          />
                        )}

                        {/* Priority Indicator */}
                        <div className="flex flex-col items-center gap-1 pt-1">
                          <div className={`w-3 h-3 rounded-full ${PRIORITY_COLORS[note.priority]}`}></div>
                          {note.is_pinned && <Pin className="h-4 w-4 text-blue-600" />}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-lg truncate">{note.title}</h3>
                              {note.project_name && (
                                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                  <Folder className="h-3 w-3" />
                                  {note.project_name}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {new Date(note.updated_at).toLocaleDateString()}
                            </div>
                          </div>

                          <div className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            <NoteContentRenderer content={note.content || 'No content'} references={note.references || []} showCards={false} />
                          </div>

                          {/* Tags, Sharing, and Actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-wrap">
                              {note.tags && note.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {note.tags.slice(0, 4).map((tag, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {note.tags.length > 4 && (
                                    <Badge variant="outline" className="text-xs">+{note.tags.length - 4}</Badge>
                                  )}
                                </div>
                              )}

                              {note.member_details && note.member_details.length > 0 && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Users className="h-3 w-3" />
                                  <span>{note.member_details.length} shared</span>
                                </div>
                              )}

                              {note.assigned_to_details && (
                                <Badge variant="secondary" className="text-xs">
                                  Assigned: {note.assigned_to_details.first_name}
                                </Badge>
                              )}
                            </div>

                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportNote(note.id, note.title);
                                }}
                                title="Export"
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveNote(note.id, !note.is_archived);
                                }}
                                title={note.is_archived ? 'Restore' : 'Archive'}
                              >
                                <Archive className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  speakText(note.content);
                                }}
                                title="Read"
                              >
                                <Volume2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteNote(note.id);
                                }}
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProject ? 'Edit Project' : 'Create New Project'}
            </DialogTitle>
            <DialogDescription>
              {editingProject ? 'Update project details' : 'Organize your notes with projects'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="My Project"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                placeholder="Project description..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={projectForm.color}
                  onChange={(e) => setProjectForm({ ...projectForm, color: e.target.value })}
                  className="w-20 h-10"
                />
                <Input
                  value={projectForm.color}
                  onChange={(e) => setProjectForm({ ...projectForm, color: e.target.value })}
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            {/* Project Members */}
            {editingProject && (
              <div className="border-t pt-4 space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Share Project with Members or Teams
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={selectedMember}
                    onValueChange={(value) => {
                      if (value) {
                        if (value.startsWith('user-')) {
                          const userId = value.replace('user-', '');
                          addMemberToProject(editingProject.id, userId);
                        } else if (value.startsWith('team-')) {
                          const teamId = value.replace('team-', '');
                          addTeamToProject(editingProject.id, teamId);
                        }
                        setSelectedMember('');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select member or team..." />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingUsers ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : (
                        <>
                          <SelectItem value="header-members" disabled className="font-semibold">
                            Individual Members
                          </SelectItem>
                          {allUsers
                            .filter(user => !editingProject.members?.includes(user.user_id))
                            .map(user => (
                              <SelectItem key={`user-${user.user_id}`} value={`user-${user.user_id}`}>
                                👤 {user.first_name} {user.last_name}
                              </SelectItem>
                            ))}
                          
                          <SelectItem value="header-teams" disabled className="font-semibold mt-2">
                            Entire Teams
                          </SelectItem>
                          {allTeams
                            .filter(team => !editingProject.assigned_teams?.includes(team.id))
                            .map(team => (
                              <SelectItem key={`team-${team.id}`} value={`team-${team.id}`}>
                                👥 {team.name}
                              </SelectItem>
                            ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Display All Shared Members and Teams */}
                <div className="space-y-2">
                  <Label>Currently Shared With:</Label>
                  <div className="flex flex-wrap gap-2 min-h-[2rem] p-2 border rounded-lg bg-muted/30">
                    {/* Individual Members */}
                    {editingProject.member_details?.map((member) => (
                      <Badge key={member.user_id} variant="secondary" className="flex items-center gap-1">
                        👤 {member.first_name} {member.last_name}
                        <button
                          onClick={() => removeMemberFromProject(editingProject.id, member.user_id)}
                          className="ml-1 hover:text-red-600"
                          title="Remove member"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    
                    {/* Teams */}
                    {editingProject.assigned_team_details?.map((team) => (
                      <Badge 
                        key={team.id} 
                        variant="outline" 
                        className="flex items-center gap-1"
                        style={{ borderColor: team.color }}
                      >
                        👥 {team.name} Team
                        <button
                          onClick={() => removeTeamFromProject(editingProject.id, team.id)}
                          className="ml-1 hover:text-red-600"
                          title="Remove team"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}

                    {/* Empty State */}
                    {(!editingProject.member_details || editingProject.member_details.length === 0) &&
                     (!editingProject.assigned_team_details || editingProject.assigned_team_details.length === 0) && (
                      <span className="text-sm text-muted-foreground italic">
                        Not shared with anyone yet
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  👤 = Individual member • 👥 = Entire team • All can view and edit notes in this project
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={editingProject ? handleUpdateProject : handleCreateProject}>
              {editingProject ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedNote ? 'Edit Note' : 'Create New Note'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                placeholder="Note title..."
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Content</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={isListening ? stopListening : startListening}
                  >
                    {isListening ? (
                      <>
                        <MicOff className="h-4 w-4 mr-2" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Mic className="h-4 w-4 mr-2" />
                        Speech
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => isSpeaking ? stopSpeaking() : speakText(noteForm.content)}
                  >
                    <Volume2 className="h-4 w-4 mr-2" />
                    {isSpeaking ? 'Stop' : 'Speak'}
                  </Button>
                </div>
              </div>
              <MentionTextarea
                value={noteForm.content}
                onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                placeholder="Write your note here... Type @ for members, #J for jobs, #C for contacts"
                rows={10}
                references={noteForm.references}
                onReferencesChange={(newRefs) => setNoteForm({ ...noteForm, references: newRefs })}
              />
              
              {/* Show reference cards for jobs and contacts */}
              {noteForm.references && noteForm.references.filter(r => r.type === 'job' || r.type === 'contact').length > 0 && (
                <div className="mt-2 space-y-1">
                  {noteForm.references
                    .filter(r => r.type === 'job' || r.type === 'contact')
                    .map((ref, idx) => (
                      <ReferenceCard key={`${ref.type}-${ref.id}-${idx}`} reference={ref} compact={false} />
                    ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={noteForm.project_id || 'none'}
                  onValueChange={(val) => setNoteForm({ ...noteForm, project_id: val === 'none' ? null : val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={noteForm.priority}
                  onValueChange={(val) => setNoteForm({ ...noteForm, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag and press Enter"
                />
                <Button type="button" onClick={addTag}>
                  <Tag className="h-4 w-4" />
                </Button>
              </div>
              {noteForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {noteForm.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary">
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pinned"
                checked={noteForm.is_pinned}
                onChange={(e) => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="pinned" className="cursor-pointer">
                Pin this note to the top
              </Label>
            </div>

            {/* Collaboration Section */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Collaboration & Assignment
              </h3>
              
              {/* Add Members or Teams */}
              <div className="space-y-2">
                <Label>Add Team Members or Entire Teams</Label>
                <div className="flex gap-2">
                  <Select
                    value={selectedAssignee}
                    onValueChange={(value) => {
                      if (value) {
                        if (selectedNote) {
                          // Editing existing note - use API calls
                          if (value.startsWith('user-')) {
                            const userId = value.replace('user-', '');
                            addMemberToNote(selectedNote.id, userId);
                          } else if (value.startsWith('team-')) {
                            const teamId = value.replace('team-', '');
                            addTeamToNote(selectedNote.id, teamId);
                          } else if (value.startsWith('assign-')) {
                            const userId = value.replace('assign-', '');
                            handleAssignNote(selectedNote.id, userId);
                          }
                        } else {
                          // Creating new note - update form state
                          if (value.startsWith('user-')) {
                            const userId = value.replace('user-', '');
                            const currentMembers = noteForm.members || [];
                            if (!currentMembers.includes(userId)) {
                              setNoteForm({ ...noteForm, members: [...currentMembers, userId] });
                            }
                          } else if (value.startsWith('team-')) {
                            const teamId = value.replace('team-', '');
                            const currentTeams = noteForm.teams || [];
                            if (!currentTeams.includes(teamId)) {
                              setNoteForm({ ...noteForm, teams: [...currentTeams, teamId] });
                            }
                          } else if (value.startsWith('assign-')) {
                            const userId = value.replace('assign-', '');
                            setNoteForm({ ...noteForm, assigned_to: userId });
                          }
                        }
                        setSelectedAssignee('');
                      }
                    }}
                  >
                      <SelectTrigger>
                        <SelectValue placeholder="Select member or team..." />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingUsers ? (
                          <SelectItem value="loading" disabled>Loading...</SelectItem>
                        ) : (
                          <>
                            <SelectItem value="header-assign" disabled className="font-semibold">
                              Assign To Individual
                            </SelectItem>
                            {allUsers
                              .filter(user => {
                                const currentAssigned = selectedNote ? selectedNote.assigned_to : noteForm.assigned_to;
                                return currentAssigned !== user.user_id;
                              })
                              .map(user => (
                                <SelectItem key={`assign-${user.user_id}`} value={`assign-${user.user_id}`}>
                                  👤 {user.first_name} {user.last_name}
                                </SelectItem>
                              ))}
                            
                            <SelectItem value="header-share" disabled className="font-semibold mt-2">
                              Share with Member
                            </SelectItem>
                            {allUsers
                              .filter(user => {
                                const currentMembers = selectedNote ? selectedNote.members : (noteForm.members || []);
                                return !currentMembers?.includes(user.user_id);
                              })
                              .map(user => (
                                <SelectItem key={`user-${user.user_id}`} value={`user-${user.user_id}`}>
                                  👤 {user.first_name} {user.last_name}
                                </SelectItem>
                              ))}
                            
                            <SelectItem value="header-teams" disabled className="font-semibold mt-2">
                              Share with Entire Team
                            </SelectItem>
                            {allTeams
                              .filter(team => {
                                const currentTeams = selectedNote ? selectedNote.assigned_teams : (noteForm.teams || []);
                                return !currentTeams?.includes(team.id);
                              })
                              .map(team => (
                                <SelectItem key={`team-${team.id}`} value={`team-${team.id}`}>
                                  👥 {team.name}
                                </SelectItem>
                              ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Display All Assigned Members and Teams */}
                <div className="space-y-2">
                  <Label>Currently Shared With:</Label>
                  <div className="flex flex-wrap gap-2 min-h-[2rem] p-2 border rounded-lg bg-muted/30">
                    {/* Assigned To */}
                    {(selectedNote ? selectedNote.assigned_to_details : (noteForm.assigned_to && allUsers.find(u => u.user_id === noteForm.assigned_to))) && (
                      <Badge variant="default" className="flex items-center gap-1">
                        👤 {selectedNote ? 
                          `${selectedNote.assigned_to_details.first_name} ${selectedNote.assigned_to_details.last_name}` :
                          (() => {
                            const user = allUsers.find(u => u.user_id === noteForm.assigned_to);
                            return user ? `${user.first_name} ${user.last_name}` : 'Unknown';
                          })()
                        }
                        <span className="text-xs opacity-70">(Assigned)</span>
                        <button
                          onClick={() => {
                            if (selectedNote) {
                              handleAssignNote(selectedNote.id, null);
                            } else {
                              setNoteForm({ ...noteForm, assigned_to: null });
                            }
                          }}
                          className="ml-1 hover:text-red-200"
                          title="Unassign"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    
                    {/* Individual Members */}
                    {(selectedNote ? selectedNote.member_details : (noteForm.members || []).map(userId => allUsers.find(u => u.user_id === userId)).filter(Boolean))?.map((member) => (
                      <Badge key={member.user_id} variant="secondary" className="flex items-center gap-1">
                        👤 {member.first_name} {member.last_name}
                        <button
                          onClick={() => {
                            if (selectedNote) {
                              removeMemberFromNote(selectedNote.id, member.user_id);
                            } else {
                              setNoteForm({ ...noteForm, members: (noteForm.members || []).filter(id => id !== member.user_id) });
                            }
                          }}
                          className="ml-1 hover:text-red-600"
                          title="Remove member"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    
                    {/* Teams */}
                    {(selectedNote ? selectedNote.assigned_team_details : (noteForm.teams || []).map(teamId => allTeams.find(t => t.id === teamId)).filter(Boolean))?.map((team) => (
                      <Badge 
                        key={team.id} 
                        variant="outline" 
                        className="flex items-center gap-1"
                        style={{ borderColor: team.color }}
                      >
                        👥 {team.name} Team
                        <button
                          onClick={() => {
                            if (selectedNote) {
                              removeTeamFromNote(selectedNote.id, team.id);
                            } else {
                              setNoteForm({ ...noteForm, teams: (noteForm.teams || []).filter(id => id !== team.id) });
                            }
                          }}
                          className="ml-1 hover:text-red-600"
                          title="Remove team"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}

                    {/* Empty State */}
                    {(() => {
                      const hasAssigned = selectedNote ? selectedNote.assigned_to_details : noteForm.assigned_to;
                      const hasMembers = selectedNote ? (selectedNote.member_details && selectedNote.member_details.length > 0) : (noteForm.members && noteForm.members.length > 0);
                      const hasTeams = selectedNote ? (selectedNote.assigned_team_details && selectedNote.assigned_team_details.length > 0) : (noteForm.teams && noteForm.teams.length > 0);
                      return !hasAssigned && !hasMembers && !hasTeams && (
                        <span className="text-sm text-muted-foreground italic">
                          No members or teams assigned yet
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    👤 = Individual member • 👥 = Entire team • All can view and edit
                  </p>
                </div>
              </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={selectedNote ? handleUpdateNote : handleCreateNote}>
              <Save className="h-4 w-4 mr-2" />
              {selectedNote ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'move' ? 'Move Notes to Project' : 'Add Tags to Notes'}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'move' 
                ? `Move ${selectedNotes.length} note(s) to a project`
                : `Add tags to ${selectedNotes.length} note(s)`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {bulkAction === 'move' ? (
              <div>
                <Label>Select Project</Label>
                <Select 
                  value={bulkActionData.project_id} 
                  onValueChange={(value) => setBulkActionData({ ...bulkActionData, project_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: project.color }}
                          ></div>
                          {project.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  placeholder="e.g., urgent, important, review"
                  value={bulkActionData.tags}
                  onChange={(e) => setBulkActionData({ ...bulkActionData, tags: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter multiple tags separated by commas
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBulkActionDialogOpen(false);
              setBulkActionData({ project_id: '', tags: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={bulkAction === 'move' ? handleBulkMove : handleBulkTag}>
              {bulkAction === 'move' ? 'Move Notes' : 'Add Tags'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notes Statistics</DialogTitle>
            <DialogDescription>
              Overview of your notes activity and organization
            </DialogDescription>
          </DialogHeader>

          {stats && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-muted-foreground">Total Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_notes}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-muted-foreground">Active</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{stats.active_notes}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-muted-foreground">Archived</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-600">{stats.archived_notes}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-muted-foreground">Pinned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{stats.pinned_notes}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Priority Distribution */}
              <div>
                <h3 className="font-semibold mb-3">Notes by Priority</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-950">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>High Priority</span>
                    </div>
                    <Badge variant="secondary">{stats.by_priority?.high || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded bg-yellow-50 dark:bg-yellow-950">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <span>Medium Priority</span>
                    </div>
                    <Badge variant="secondary">{stats.by_priority?.medium || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span>Low Priority</span>
                    </div>
                    <Badge variant="secondary">{stats.by_priority?.low || 0}</Badge>
                  </div>
                </div>
              </div>

              {/* Project Distribution */}
              {stats.by_project && Object.keys(stats.by_project).length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Notes by Project</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.by_project).map(([projectName, count]) => (
                      <div key={projectName} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <div className="flex items-center gap-2">
                          <Folder className="h-4 w-4" />
                          <span>{projectName}</span>
                        </div>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Tags */}
              {stats.top_tags && Object.keys(stats.top_tags).length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Most Used Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.top_tags).map(([tag, count]) => (
                      <Badge key={tag} variant="secondary" className="text-sm">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag} ({count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setStatsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
